/**
 * StorageVolumesPanel — unidades que ve un servidor/VM (D:, /mnt/datos…).
 *
 * Es la otra cara del LUN de la cabina: aquí se documenta qué unidad ve el
 * equipo, de qué LUN viene, su IQN de iniciador y si va por multipath. Un mismo
 * LUN puede estar en varios servidores (clúster), por eso el LUN se elige del
 * catálogo de cabinas en vez de escribirlo.
 *
 * El panel es reutilizable: vale para servidores físicos (ci_type=rack_element),
 * máquinas virtuales (virt_vm) y puestos (workstation).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusIcon, TrashIcon, PencilSquareIcon, XMarkIcon, ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const PROTOCOLS = [
  ['iscsi', 'iSCSI'],
  ['fc', 'Fibre Channel'],
  ['nvmeof', 'NVMe-oF'],
  ['nfs', 'NFS'],
  ['smb', 'SMB / CIFS'],
  ['local', 'Local (DAS)'],
  ['other', 'Otro'],
];

const PROTO_LABEL = Object.fromEntries(PROTOCOLS);
const protoLabel = (v) => PROTO_LABEL[v] || (v || '').toUpperCase();

const EMPTY_VOLUME = {
  mount_point: '', label: '', filesystem: '', size_gb: '', used_gb: '',
  protocol: '', initiator_iqn: '', is_multipath: false, path_count: '',
  access_mode: 'rw', is_clustered: false, status: 'online', notes: '', lun_id: '',
};

const th = {
  textAlign: 'left', padding: '5px 8px', color: 'var(--text-muted)',
  borderBottom: '1px solid var(--slate-300)', whiteSpace: 'nowrap', fontSize: '0.72rem',
};
const td = {
  padding: '5px 8px', borderBottom: '1px solid var(--slate-200)',
  color: 'var(--text-primary)', fontSize: '0.8rem', verticalAlign: 'top',
};

function fmtGb(value) {
  if (value == null || value === '') return '—';
  const gb = Number(value);
  if (!Number.isFinite(gb)) return '—';
  if (gb >= 1024) return `${(gb / 1024).toLocaleString('es-ES', { maximumFractionDigits: 2 })} TB`;
  return `${gb.toLocaleString('es-ES')} GB`;
}

export default function StorageVolumesPanel({
  ciType = 'rack_element', ciId, companyId, luns = [], onChanged, onOpenElement,
}) {
  const [volumes, setVolumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!ciId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getVolumes(companyId, ciType, ciId);
      setVolumes(res.data || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los volúmenes');
    } finally {
      setLoading(false);
    }
  }, [companyId, ciType, ciId]);

  useEffect(() => { load(); }, [load]);

  const lunsByElement = () => {
    const map = {};
    (luns || []).forEach(l => {
      const key = l.element_name || `Cabina ${l.element_id}`;
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return map;
  };

  const openNew = () => setEditing({ ...EMPTY_VOLUME });
  const openEdit = (v) => setEditing({
    id: v.id,
    mount_point: v.mount_point || '',
    label: v.label || '',
    filesystem: v.filesystem || '',
    size_gb: v.size_gb ?? '',
    used_gb: v.used_gb ?? '',
    protocol: v.protocol || '',
    initiator_iqn: v.initiator_iqn || '',
    is_multipath: !!v.is_multipath,
    path_count: v.path_count ?? '',
    access_mode: v.access_mode || 'rw',
    is_clustered: !!v.is_clustered,
    status: v.status || 'online',
    notes: v.notes || '',
    lun_id: v.lun_id ?? '',
  });

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...editing,
        ci_type: ciType,
        ci_id: ciId,
        lun_id: editing.lun_id === '' ? null : Number(editing.lun_id),
        size_gb: editing.size_gb === '' ? null : Number(editing.size_gb),
        used_gb: editing.used_gb === '' ? null : Number(editing.used_gb),
        path_count: editing.path_count === '' ? null : Number(editing.path_count),
      };
      if (editing.id) await api.updateVolume(editing.id, payload, companyId);
      else await api.createVolume(payload, companyId);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo guardar el volumen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (volume) => {
    if (!window.confirm(`¿Quitar el volumen ${volume.mount_point || volume.label || ''}?`)) return;
    setError('');
    try {
      await api.deleteVolume(volume.id, companyId);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el volumen');
    }
  };

  const selectedLun = (luns || []).find(l => String(l.id) === String(editing?.lun_id));
  const grouped = lunsByElement();

  return (
    <div className="dv-section" style={{
      background: 'rgba(8,145,178,.06)', border: '1px solid rgba(8,145,178,.3)',
      borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Unidades y volúmenes de este equipo
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {volumes.length}
          </span>
        </div>
        {!editing && (
          <button className="dv-btn" type="button" onClick={openNew}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir volumen
          </button>
        )}
      </div>

      <p className="dv-hint" style={{ marginBottom: 10 }}>
        Las unidades locales del inventario del equipo aparecen aquí automáticamente (las
        detectadas por el agente se marcan como tal). Documenta además las unidades externas
        (iSCSI/FC/NFS…) y, si vienen de una cabina, elige su <strong>LUN de origen</strong>. Con eso
        el diseñador de servicios dibuja la cadena <strong>equipo → volumen → LUN → cabina</strong>.
        El IQN del iniciador es el del servidor.
      </p>

      {error && (
        <div style={{
          color: '#dc2626', fontSize: '0.78rem', marginBottom: 8,
          background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.3)',
          borderRadius: 6, padding: '6px 8px',
        }}>
          ⚠ {error}
        </div>
      )}

      {editing && (
        <div style={{
          border: '1px solid var(--slate-300)', borderRadius: 8, padding: 10,
          background: 'var(--bg-card)', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ flex: 1, fontSize: '0.8rem' }}>
              {editing.id ? `Editar volumen: ${editing.mount_point || editing.label}` : 'Nuevo volumen'}
            </strong>
            <button className="dv-btn" type="button" onClick={() => setEditing(null)} title="Cerrar">
              <XMarkIcon style={{ width: 14, height: 14 }} />
            </button>
          </div>

          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Letra / ruta</label>
              <input className="form-control form-control-sm" placeholder="D: · /mnt/datos"
                     value={editing.mount_point}
                     onChange={e => setEditing({ ...editing, mount_point: e.target.value })} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Etiqueta</label>
              <input className="form-control form-control-sm" placeholder="ej. Datos SQL"
                     value={editing.label}
                     onChange={e => setEditing({ ...editing, label: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Sistema de ficheros</label>
              <input className="form-control form-control-sm" placeholder="NTFS / ext4 / VMFS"
                     value={editing.filesystem}
                     onChange={e => setEditing({ ...editing, filesystem: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Tamaño (GB)</label>
              <input className="form-control form-control-sm" type="number" min="0"
                     value={editing.size_gb}
                     onChange={e => setEditing({ ...editing, size_gb: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Usado (GB)</label>
              <input className="form-control form-control-sm" type="number" min="0"
                     value={editing.used_gb}
                     onChange={e => setEditing({ ...editing, used_gb: e.target.value })} />
            </div>

            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>LUN de origen (cabina)</label>
              <select className="form-select form-select-sm"
                      value={editing.lun_id ?? ''}
                      onChange={e => {
                        const lunId = e.target.value;
                        const lun = (luns || []).find(l => String(l.id) === String(lunId));
                        setEditing({
                          ...editing,
                          lun_id: lunId,
                          protocol: lun ? lun.protocol : editing.protocol,
                          size_gb: editing.size_gb || (lun?.size_gb ?? ''),
                        });
                      }}>
                <option value="">— Disco local / sin LUN —</option>
                {Object.entries(grouped).map(([elementName, rows]) => (
                  <optgroup key={elementName} label={elementName}>
                    {rows.map(l => (
                      <option key={l.id} value={l.id}>
                        {l.name}{l.size_gb ? ` · ${fmtGb(l.size_gb)}` : ''}{l.is_shared ? ' · compartido' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {selectedLun && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  {selectedLun.element_name} · protocolo {protoLabel(selectedLun.protocol)}
                  {selectedLun.target_iqn ? ` · target ${selectedLun.target_iqn}` : ''}
                  {' '}· presentado a {(selectedLun.presented_count || 0) + (editing.id ? 0 : 1)} volumen(es)
                  {onOpenElement && selectedLun.element_id && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        onClick={() => onOpenElement('storage', selectedLun.element_id)}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          color: 'var(--accent)', textDecoration: 'underline', fontSize: '0.72rem',
                        }}
                      >
                        abrir cabina <ArrowTopRightOnSquareIcon style={{ width: 11, height: 11, display: 'inline' }} />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Protocolo</label>
              <select className="form-select form-select-sm" value={editing.protocol}
                      onChange={e => setEditing({ ...editing, protocol: e.target.value })}>
                <option value="">—</option>
                {PROTOCOLS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Modo</label>
              <select className="form-select form-select-sm" value={editing.access_mode}
                      onChange={e => setEditing({ ...editing, access_mode: e.target.value })}>
                <option value="rw">Lectura/escritura</option>
                <option value="ro">Sólo lectura</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>IQN del iniciador (este equipo)</label>
              <input className="form-control form-control-sm" style={{ fontFamily: 'monospace', fontSize: 12 }}
                     placeholder="iqn.1991-05.com.microsoft:srv-sql.corp.local"
                     value={editing.initiator_iqn}
                     onChange={e => setEditing({ ...editing, initiator_iqn: e.target.value })} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Nº de caminos</label>
              <input className="form-control form-control-sm" type="number" min="1"
                     placeholder="ej. 2"
                     value={editing.path_count}
                     onChange={e => setEditing({ ...editing, path_count: e.target.value })} />
            </div>
            <div className="col-md-3 d-flex align-items-center" style={{ gap: 12, paddingTop: 18 }}>
              <label style={{ fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={editing.is_multipath}
                       onChange={e => setEditing({ ...editing, is_multipath: e.target.checked })} />
                Multipath (MPIO)
              </label>
              <label style={{ fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={editing.is_clustered}
                       onChange={e => setEditing({ ...editing, is_clustered: e.target.checked })} />
                Compartido (clúster)
              </label>
            </div>
            <div className="col-12">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Notas</label>
              <input className="form-control form-control-sm"
                     placeholder="ej. unidad de datos del ERP, montada por el servicio SQL"
                     value={editing.notes}
                     onChange={e => setEditing({ ...editing, notes: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button className="dv-btn" type="button" onClick={() => setEditing(null)}>Cancelar</button>
            <button className="dv-btn dv-btn-primary" type="button" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : (editing.id ? 'Guardar cambios' : 'Añadir volumen')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Cargando volúmenes…</div>
      ) : volumes.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin volúmenes documentados. Añade las unidades del equipo y, si son de una cabina,
          enlázalas con su LUN.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Unidad', 'Etiqueta', 'FS', 'Tamaño', 'Uso', 'Origen (LUN)', 'Iniciador / caminos', ''].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {volumes.map(v => (
                <tr key={v.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{v.mount_point || '—'}</td>
                  <td style={td}>{v.label || '—'}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>{v.filesystem || '—'}</td>
                  <td style={td}>{fmtGb(v.size_gb)}</td>
                  <td style={td}>
                    {v.usage_pct == null ? '—' : (
                      <span title={`${fmtGb(v.used_gb)} usados de ${fmtGb(v.size_gb)}`}>
                        {v.usage_pct}%
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    {v.lun ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{v.lun.name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {v.lun.element_name || 'cabina'}
                          {v.protocol ? ` · ${protoLabel(v.protocol)}` : ''}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                        {v.protocol ? protoLabel(v.protocol) : 'local'}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    {v.initiator_iqn ? (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.66rem' }}>{v.initiator_iqn}</div>
                    ) : null}
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {v.is_multipath ? `multipath · ${v.path_count || '?'} caminos` : (v.path_count ? `${v.path_count} camino(s)` : '')}
                      {v.is_clustered ? ' · compartido' : ''}
                      {v.source === 'agent'
                        ? ' · detectado por agente'
                        : (v.source === 'inventory' ? ' · inventario' : '')}
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button className="dv-btn" type="button" title="Editar" onClick={() => openEdit(v)}>
                      <PencilSquareIcon style={{ width: 14, height: 14 }} />
                    </button>
                    <button className="dv-btn" type="button" title="Quitar" onClick={() => remove(v)}
                            style={{ marginLeft: 4 }}>
                      <TrashIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
